import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import { Progress } from '../../ui/progress';
import { AlertTriangle, CheckCircle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { 
  SRS22R_CONFIG, 
  SRS22R_DOMAINS,
  validateSRS22RAnswers, 
  calculateSRS22RScore,
  getDomainColor,
  getDomainName
} from './srs22rSchema';

/**
 * SRS22RQuestionnaire - Componente para el cuestionario SRS-22r de Escoliosis
 * 
 * Props:
 * - onComplete: callback con los resultados
 * - initialAnswers: respuestas previas (para edición)
 * - config: configuración personalizada de textos
 * - allowIncomplete: permitir guardar incompleto (para médicos)
 * - showDomainProgress: mostrar progreso por dominio
 * - twoPageMode: dividir en 2 páginas (11+11)
 */
const SRS22RQuestionnaire = ({ 
  onComplete, 
  initialAnswers = {}, 
  config = SRS22R_CONFIG,
  allowIncomplete = false,
  showDomainProgress = true,
  twoPageMode = false
}) => {
  const [answers, setAnswers] = useState(() => {
    // Inicializar con respuestas previas o vacío
    const initial = {};
    for (let i = 1; i <= 22; i++) {
      initial[`item${i}`] = initialAnswers[`item${i}`] ?? null;
    }
    return initial;
  });
  
  const [errors, setErrors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedDomains, setExpandedDomains] = useState({});

  // Calcular progreso
  const answeredCount = Object.values(answers).filter(v => v !== null).length;
  const progress = (answeredCount / 22) * 100;

  // Calcular progreso por dominio
  const domainProgress = {};
  Object.entries(SRS22R_DOMAINS).forEach(([key, domain]) => {
    const answered = domain.items.filter(i => answers[`item${i}`] !== null).length;
    domainProgress[key] = {
      answered,
      total: domain.items.length,
      percentage: Math.round((answered / domain.items.length) * 100)
    };
  });

  // Manejar cambio de respuesta
  const handleAnswerChange = (itemNumber, value) => {
    setAnswers(prev => ({
      ...prev,
      [`item${itemNumber}`]: value
    }));
    // Limpiar errores al responder
    setErrors([]);
  };

  // Manejar submit
  const handleSubmit = () => {
    const validation = validateSRS22RAnswers(answers, allowIncomplete);
    
    if (!validation.isValid && !allowIncomplete) {
      setErrors(validation.errors);
      return;
    }
    
    setErrors([]);
    
    const scores = calculateSRS22RScore(answers, config);
    
    onComplete({
      answers,
      ...scores,
      questionnaire_type: 'SRS22R',
      version: config.version,
      completed_at: new Date().toISOString(),
      is_draft: !validation.isValid && allowIncomplete
    });
  };

  // Obtener ítems para la página actual (si twoPageMode)
  const getItemsForPage = (page) => {
    if (!twoPageMode) return Array.from({ length: 22 }, (_, i) => i + 1);
    return page === 1 
      ? Array.from({ length: 11 }, (_, i) => i + 1)
      : Array.from({ length: 11 }, (_, i) => i + 12);
  };

  const currentItems = getItemsForPage(currentPage);

  // Preview de puntuación
  const previewScores = calculateSRS22RScore(answers, config);

  // Obtener opciones para un ítem
  const getItemOptions = (itemNumber) => {
    const itemConfig = config.texts.items[`item${itemNumber}`];
    return itemConfig?.options || config.texts.defaultOptions;
  };

  // Obtener texto del ítem
  const getItemText = (itemNumber) => {
    const itemConfig = config.texts.items[`item${itemNumber}`];
    return itemConfig?.text || `[Pregunta ${itemNumber}]`;
  };

  // Obtener dominio del ítem
  const getItemDomain = (itemNumber) => {
    for (const [key, domain] of Object.entries(SRS22R_DOMAINS)) {
      if (domain.items.includes(itemNumber)) {
        return { key, ...domain };
      }
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Cabecera con instrucciones */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-indigo-900">{config.texts.title}</h3>
            <p className="text-sm text-indigo-700 mt-1">{config.texts.instructions}</p>
          </div>
        </div>
      </div>

      {/* Nota de licencia */}
      <Alert className="bg-amber-50 border-amber-200">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-700 text-sm">
          <strong>Nota:</strong> {config.texts.licenseNote}
        </AlertDescription>
      </Alert>

      {/* Barra de progreso general */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Progreso</span>
          <span className="font-medium">{answeredCount} de 22 preguntas</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Progreso por dominio */}
      {showDomainProgress && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {Object.entries(SRS22R_DOMAINS).map(([key, domain]) => (
            <div 
              key={key}
              className="p-2 rounded-lg border text-center"
              style={{ borderColor: `${domain.color}40`, backgroundColor: `${domain.color}10` }}
            >
              <div className="text-xs font-medium" style={{ color: domain.color }}>
                {domain.nameShort}
              </div>
              <div className="text-sm font-bold" style={{ color: domain.color }}>
                {domainProgress[key].answered}/{domainProgress[key].total}
              </div>
              <div 
                className="h-1 rounded-full mt-1" 
                style={{ backgroundColor: `${domain.color}30` }}
              >
                <div 
                  className="h-full rounded-full transition-all"
                  style={{ 
                    width: `${domainProgress[key].percentage}%`,
                    backgroundColor: domain.color 
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preguntas */}
      <div className="space-y-4">
        {twoPageMode && (
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className={`px-3 py-1 rounded-full text-sm ${currentPage === 1 ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
              Parte 1 (1-11)
            </span>
            <span className={`px-3 py-1 rounded-full text-sm ${currentPage === 2 ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
              Parte 2 (12-22)
            </span>
          </div>
        )}

        {currentItems.map((itemNumber) => {
          const isAnswered = answers[`item${itemNumber}`] !== null;
          const domain = getItemDomain(itemNumber);
          const itemText = getItemText(itemNumber);
          const options = getItemOptions(itemNumber);

          return (
            <Card 
              key={itemNumber}
              className={`border-2 transition-colors ${
                isAnswered 
                  ? 'border-green-200 bg-green-50/30' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      isAnswered ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {isAnswered ? <CheckCircle className="w-4 h-4" /> : itemNumber}
                    </span>
                    <span className="text-gray-900">Pregunta {itemNumber}</span>
                  </div>
                  {domain && (
                    <span 
                      className="text-xs px-2 py-1 rounded-full"
                      style={{ 
                        backgroundColor: `${domain.color}20`,
                        color: domain.color 
                      }}
                    >
                      {domain.nameShort}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-gray-700 mb-4 ml-10">{itemText}</p>
                
                <div className="ml-10 space-y-2">
                  {options.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        answers[`item${itemNumber}`] === option.value
                          ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`item${itemNumber}`}
                        value={option.value}
                        checked={answers[`item${itemNumber}`] === option.value}
                        onChange={() => handleAnswerChange(itemNumber, option.value)}
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className={`flex-1 text-sm ${
                        answers[`item${itemNumber}`] === option.value 
                          ? 'font-medium text-indigo-900' 
                          : 'text-gray-700'
                      }`}>
                        {option.label}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        option.value === 1 ? 'bg-red-100 text-red-700' :
                        option.value === 2 ? 'bg-orange-100 text-orange-700' :
                        option.value === 3 ? 'bg-yellow-100 text-yellow-700' :
                        option.value === 4 ? 'bg-lime-100 text-lime-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {option.value}
                      </span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Navegación entre páginas (si twoPageMode) */}
      {twoPageMode && (
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            ← Parte 1
          </Button>
          <Button
            variant="outline"
            onClick={() => setCurrentPage(2)}
            disabled={currentPage === 2}
          >
            Parte 2 →
          </Button>
        </div>
      )}

      {/* Preview de puntuaciones */}
      {answeredCount > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 border">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">Puntuaciones parciales</h4>
            {previewScores.subtotal_score && (
              <div className="text-right">
                <p className="text-2xl font-bold text-indigo-600">
                  {previewScores.subtotal_score.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500">Score global (sin satisfacción)</p>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(SRS22R_DOMAINS).map(([key, domain]) => {
              const score = previewScores.domain_scores[key];
              return (
                <div 
                  key={key}
                  className="p-3 rounded-lg border"
                  style={{ borderColor: `${domain.color}40` }}
                >
                  <p className="text-xs text-gray-600">{domain.nameShort}</p>
                  <p className="text-xl font-bold" style={{ color: domain.color }}>
                    {score?.score?.toFixed(2) || '—'}
                  </p>
                  <p className="text-xs text-gray-400">
                    {score?.answered || 0}/{score?.total || domain.items.length}
                  </p>
                </div>
              );
            })}
          </div>

          {previewScores.interpretation && (
            <p className="mt-3 text-sm text-center text-gray-600">
              <strong>Interpretación:</strong> {previewScores.interpretation}
            </p>
          )}
        </div>
      )}

      {/* Errores */}
      {errors.length > 0 && (
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-700">
            <p className="font-medium mb-1">Por favor complete las siguientes preguntas:</p>
            <p className="text-sm">
              {errors.slice(0, 5).map(e => e.replace('El ítem ', '')).join(', ')}
              {errors.length > 5 && ` y ${errors.length - 5} más`}
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Botón guardar */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        {allowIncomplete && answeredCount > 0 && answeredCount < 22 && (
          <Button
            variant="outline"
            onClick={handleSubmit}
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            Guardar borrador ({answeredCount}/22)
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          className="bg-indigo-600 hover:bg-indigo-700"
          disabled={answeredCount < 22 && !allowIncomplete}
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          Guardar Cuestionario
        </Button>
      </div>
    </div>
  );
};

export default SRS22RQuestionnaire;
